/* eslint-disable camelcase */
import { isEdge } from '@ardoq/common-helpers';
import { logError, logWarn } from '@ardoq/logging';
import { APP_VERSION } from 'appConfig';

interface IntercomSettings {
  app_id: string;
  ardoq_version: string;
  api_base: string;
  alignment: string;
  horizontal_padding: number;
}

interface TrackEvent {
  action: string;
  metadata?: Record<string, unknown>;
}

interface UserDetails {
  [key: string]: unknown;
}

let initialized = false;
let identifyWhenInitialized: UserDetails | undefined;
const trackWhenLoaded: TrackEvent[] = [];

const intercom = {
  initialize: function (analyticsKey: string, supportUrl: string): void {
    try {
      // Reuse main window instance of Intercom if it exists
      if (window.parent?.IS_MAIN_ARDOQ_APP && window.parent.Intercom) {
        window.Intercom = window.parent.Intercom;
        return;
      }

      if (window.opener?.IS_MAIN_ARDOQ_APP && window.opener.Intercom) {
        window.Intercom = window.opener.Intercom;
        return;
      }
    } catch (e) {
      logWarn(Error(`Could not reuse main window Intercom instance - ${e}`));
    }
    const intercomSettings: IntercomSettings = {
      app_id: analyticsKey,
      ardoq_version: APP_VERSION,
      api_base: supportUrl,
      alignment: 'right',
      horizontal_padding: 56,
    };
    try {
      (function () {
        const w = window;
        const ic = w.Intercom;
        if (typeof ic === 'function') {
          ic('reattach_activator');
          ic('update', intercomSettings);
        } else {
          const d = document;
          const i = function (this: any, ...args: any[]) {
            (i as IntercomFunction).c?.(args);
          } as IntercomFunction;

          i.q = [];
          i.c = function (args: IArguments) {
            i.q?.push(args);
          };
          w.Intercom = i;

          function l() {
            const s = d.createElement('script');
            s.type = 'text/javascript';
            s.async = true;
            s.src = `https://widget.intercom.io/widget/${analyticsKey}`;
            const x = d.getElementsByTagName('script')[0];
            if (x && x.parentNode) {
              x.parentNode.insertBefore(s, x);
            }
          }
          l();
        }
      })();
      window.Intercom?.('boot', intercomSettings);
      initialized = true;
      if (identifyWhenInitialized) {
        this.identify(identifyWhenInitialized);
      }
      trackWhenLoaded?.forEach(({ action, metadata }) => {
        this.track(action, metadata);
      });
    } catch (error) {
      logError(error as Error, 'Failed to load Intercom');
    }
  },
  identify: function (userDetails: UserDetails): void {
    if (window.Intercom) {
      window.Intercom?.('update', userDetails);
    } else if (!initialized) {
      identifyWhenInitialized = userDetails;
    }
  },
  track: function (
    action: string,
    metadata: Record<string, unknown> = {}
  ): void {
    if (window.Intercom) {
      try {
        window.Intercom?.('trackEvent', action, {
          ...metadata,
        });
      } catch (error) {
        logError(error as Error, `Failed to track event to Intercom`, {
          action,
        });
      }
    } else {
      trackWhenLoaded.push({ action, metadata });
    }
  },
  startIntercomTour: function (tourId: number): void {
    // Intercom tours have some issues with Edge that result in Intercom "bubble"
    // messages being unclickable. This causes users to get stuck in tours.
    if (isEdge()) return;
    if (!window.Intercom) return;
    window.Intercom?.('startTour', tourId);
  },
};

export { intercom };
